import * as React from "react";
import { observer, Provider, inject } from "mobx-react";
import Editor from "../../editor/Editor";
import LinearProgress from "@mui/material/LinearProgress";
import Box from "@mui/material/Box";
import { v4 as uuid } from "uuid";
import { uniqBy } from "lodash";
import { Close } from "@mui/icons-material";

import {
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  CircularProgress,
  Avatar,
  Button,
  Dialog,
  DialogTitle,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import LogoutButton from "../common/LogoutButton";
import autobind from "autobind-decorator";
import AccountService from "../../service/AccountService";
import { RouterStore } from "mobx-react-router";
import { observable } from "mobx";
import FileService, { NarrativeMetadata } from "../../service/FileService";
import { deserializeLayerGroup } from "../../editor/serializer";
import QuestionnaireChatRenderer, {
  QuestionnaireChatState,
} from "../../editor-ui/QuestionnaireChatRenderer";
import SaveButton from "../editor/SaveButton";
import ReloadIcon from "@mui/icons-material/Refresh";
import NarrativeBuilder from "../../editor/NarrativeBuilder";
import { getPlatformType } from "../../Config";
import { Icon } from "@iconify/react";
import robotIcon from "@iconify/icons-mdi/robot";
import SendIcon from "@mui/icons-material/Send";
import PermIdentityIcon from "@mui/icons-material/PermIdentity";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { questionTypes } from "../../editor/NodeLayer";
import firebase from "firebase/compat/app";
import SendService from "../../service/sendService";
import { AttachFileRounded, Cancel } from "@mui/icons-material";
import docsImg from "../../assets/docs.png";
import { getStorage, ref, uploadBytes, deleteObject } from "firebase/storage";
import DragDropContainer from "./components/drag-drop-container/DragDropContainer";
import CustomLinkify from "./components/CustomLinkify";

const ModalButton = styled("button")({
  width: "64px",
  lineHeight: "28px",
  height: "28px",
  fontSize: "12px",
  background: "rgb(155, 66, 227)",
  color: "white",
  cursor: "pointer",
  borderRadius: "4px",
  outline: "none",
  border: "none",
  transition: "all 100ms ease-in-out",
  textAlign: "center",
});

const NextButton = styled(ModalButton)({
  backgroundColor: "#f50057",
  fontWeight: "bold",
  marginRight: "12px",
  width: "80px",
});

const ButtonRow = styled("div")({
  display: "flex",
  flexDirection: "row",
  alignItems: "center",
  justifyContent: "center",
  marginTop: "18px",
});

const Root = styled("div")({
  display: "flex",
  flexDirection: "column",
  height: "100%",
  alignItems: "center",
});

const LoaderContainer = styled("div")({
  display: "flex",
  flexDirection: "column",
  flexGrow: 1,
  alignItems: "center",
  justifyContent: "center",
});

const QuestionText = styled("p")({
  margin: "12px",
  fontSize: "32px",
  minHeight: "32px",
  color: "rgb(80, 80, 80)",
});

const ChatBody = styled("div")({
  marginBottom: "60px",
  paddingBottom: "40px",
});

const ChatFoot = styled("div")({
  minHeight: "40px",
  maxHeight: "130px",
  overflowY: "auto",
  backgroundColor: "#d3e2f0",
  color: "#5B5D65",
  alignContent: "center",
  justifyContent: "center",
  display: "flex",
  borderRadius: "20px",
});

type messageType = {
  question: string;
  answer: string;
  filesNames?: any[];
  filesLinks?: any[];
  uploadedFilesLinks?: any[];
};

type EditorPageProps = {
  routing?: RouterStore;
  account?: AccountService;
  files?: FileService;
  match?: any;
  send?: SendService;
};

type EditorPageStatus =
  | "LOADING"
  | "NORMAL"
  | "GET_NAME"
  | "NO_START_NODE"
  | "COMPLETE"
  | "UPLOAD_LOADING"
  | "CONTENT_LOADING";

@inject("account")
@inject("routing")
@inject("files")
@inject("send")
@observer
export default class QuestionnaireChatPage extends React.Component<
  EditorPageProps,
  { progress: number; responseLength: number }
> {
  private _editor = new Editor();
  private _questionnaireState = new QuestionnaireChatState(this._editor);
  state = {
    progress: 10,
    responseLength: 0,
  };

  @observable
  private _status: EditorPageStatus = "LOADING";

  @observable
  private _userData = {} as any;

  @observable
  private _fileName = "";

  @observable
  private _name = "";

  @observable
  private _qAnswer = "";

  @observable
  private _insetRef: HTMLElement;

  @observable
  messageArray: Array<messageType> = [];

  @observable
  endDialogOpen: boolean = true;

  @observable
  selectedUploadFile: any = [];

  @observable
  allUploadFiles: any = [];

  @autobind
  goBack() {
    var { account } = this.props;
    account.goBack();
  }

  @autobind
  handleKeyPress(e: any) {
    if (e.keyCode == 13) {
      this.handleNext();
    }

    e.stopPropagation();
  }

  @autobind
  handlePrevious() {
    const { userId, id } = this.props.match.params;
    const narrativeId = this.props.files.currentNarrativeId;
    const storage = getStorage();
    const storageRef = ref(storage);
    const path = `uploaded-files/${userId}/${id}/${narrativeId}`;
    const uploadedFilesRef = ref(storageRef, path);
    if (this.messageArray.length >= 1) {
      const previousFiles = this.messageArray[
        this.messageArray.length - 1
      ]?.filesNames.map((i) => i.file);
      this._qAnswer = this.messageArray[this.messageArray.length - 1]?.answer;

      this.selectedUploadFile = [...previousFiles];
      this.messageArray = this.messageArray.slice(
        0,
        this.messageArray.length - 1
      );
      this._status = "NORMAL";
      // add delete object function to delete files from the firebase storage
      this.deleteUplaodedFiles(uploadedFilesRef, previousFiles);
    } else {
      if (this._name !== "Anonymous Guest" || "" || null) {
        this.messageArray = [];
        this._qAnswer = this._name;
        this._name = "";
        this._status = "GET_NAME";
      } else {
        return;
      }
    }
    this._questionnaireState.handlePrevious();
  }

  @autobind
  async handleNext() {
    let renderAnswers = this._questionnaireState.currentQuestionNode?.answers;
    const questionType =
      this._questionnaireState.currentQuestionNode.questionType;
    const uploadedFilesNames = this.selectedUploadFile.map((file: any) => {
      return {
        name: file.name,
        file,
      };
    });
    const filesLinks = this.selectedUploadFile.map(
      (file: any) =>
        `<a download='${file.name}' href='${URL.createObjectURL(file)}'>${
          file.name
        }</a>`
    );

    if (this._status == "GET_NAME") {
      this._name = this._qAnswer;
      this._qAnswer = "";
      this._status = "NORMAL";
      return;
    }

    if (this._status == "NORMAL") {
      (this._qAnswer || uploadedFilesNames.length > 0) &&
        this._questionnaireState.currentQuestionNode.setAnswers(
          questionType === questionTypes.text
            ? [this._qAnswer]
            : [...renderAnswers]
        );
      this._questionnaireState.currentQuestionNode.setUploadedFilesNames(
        uploadedFilesNames
      );
      let tempMessage = {
        question: this._questionnaireState.currentQuestionNode.questionText,
        answer: this._questionnaireState.currentQuestionNode.answers.join("\n"),
        filesNames: uploadedFilesNames,
        filesLinks,
      };

      this.messageArray.push(tempMessage);
      this._questionnaireState.handleNext();
      this._qAnswer = "";
      this._status =
        uploadedFilesNames.length > 0 ? "UPLOAD_LOADING" : "NORMAL";
      this.selectedUploadFile = [];
      if (this._questionnaireState.isComplete) {
        this._status = "LOADING";
        await this.updateNarrativeData();
        // SEND EMAIL NOTIFICATION TO THE USER
        this._userData?.surveyNotification &&
          (await this.sendEmailNotificationHandler());
        this._status = "COMPLETE";
      }
    }
  }

  @autobind
  async saveUplaodedFiles(filesRef: any, files: any) {
    await Promise.all([
      ...files.map(async (file: any) => {
        const fileNameRef = ref(filesRef, file.name);
        return await uploadBytes(fileNameRef, file);
      }),
    ]);
  }

  @autobind
  async deleteUplaodedFiles(filesRef: any, files: any) {
    await Promise.all([
      ...files.map(async (file: any) => {
        const fileNameRef = ref(filesRef, file.name);
        await deleteObject(fileNameRef);
      }),
    ]);
  }

  @autobind
  async updateNarrativeData() {
    const { clinicId, id, userId } = this.props.match.params;
    const narrativeId = this.props.files.currentNarrativeId;
    const storage = getStorage();
    const storageRef = ref(storage);
    const uploadedFiles: any = [];
    const path = `uploaded-files/${userId}/${id}/${narrativeId}`;
    const uploadedFilesRef = ref(storageRef, path);
    var builder = new NarrativeBuilder(this._editor);
    var { narrative, responses } = builder.buildNarrative();
    const updateResponses = responses.slice(0, this.messageArray.length);
    updateResponses.forEach((res) =>
      res.filesNames.forEach(({ file }: any) => uploadedFiles.push(file))
    );
    await this.saveUplaodedFiles(uploadedFilesRef, uploadedFiles);
    const newResponses = updateResponses.map(({ q, a, filesNames }) => {
      return {
        q,
        a,
        uploadedFilesLinks: filesNames.map((file: any) => file.name),
      };
    });

    const narrativeData: NarrativeMetadata = {
      id: narrativeId,
      flowId: id,
      flowName: this._fileName,
      date: new Date().toString(),
      userName: this._name,
      narrative: narrative,
      responses: newResponses,
      clinic: clinicId,
    };
    const functions = firebase.functions();
    const updateNarrative = functions.httpsCallable("cloudUpdateNarrative");
    await updateNarrative({
      data: narrativeData,
      userId,
      currentNarrativeId: narrativeId,
    });
  }

  @autobind
  async sendEmailNotificationHandler() {
    const { id } = this.props.match.params;
    const { email } = this._userData;
    const subject = `Survey Notification`;
    const body = `
    <p>Hi,</p>
    <p>You have a new completed survey.</p>
    <p>For more details, click on the <a href='${window.location.origin}/narratives/${id}'>link</a>.</p>
    `;
    const data = {
      email,
      subject,
      body,
    };
    if (email) {
      await this.props.send.sendEmailNotification(data);
    }
  }

  @autobind
  handleEditFlow() {
    var { routing } = this.props;
    const { id } = this.props.match.params;
    routing.replace(`/editor/${id}`);
  }

  @autobind
  reload() {
    var { routing } = this.props;
    const { id, userId, clinicId } = this.props.match.params;
    routing.replace("/blank");
    let link = `/questionnaire/${userId}/${clinicId}/${id}`;
    setTimeout(() => routing.replace(link));
  }

  @autobind
  redirect() {
    var { routing } = this.props;
    const url = this._editor.startLayer?.redirectUrl;
    if (url) {
      if (url.includes("http")) window.location.href = url;
      else window.location.href = `http://${url}`;
    } else {
      routing.replace("/");
    }
  }

  @autobind
  getUploadedFile(files: any) {
    const newSelectedUploadFile = uniqBy(
      [...this.selectedUploadFile, ...Array.from(files)],
      "name"
    );
    this.selectedUploadFile = newSelectedUploadFile;
    this.allUploadFiles = uniqBy(
      [...this.allUploadFiles, ...this.selectedUploadFile],
      "name"
    );
  }

  async componentDidMount() {
    const { id, userId } = this.props.match.params;
    const { routing, files, account } = this.props;
    // new code
    const narrativeId = uuid();
    files.setCurrentNarrativeId(narrativeId);
    localStorage.setItem("Qid", id);

    if (id == null || userId == null) {
      routing.replace("/");
      return;
    }

    this._status = "LOADING";

    this._userData = (await account.fetchUserData(userId)) || {};
    var fileData = await files.loadFileDataUnsecure(id, userId);
    var fileName = await files.loadFileNameUnsecure(id, userId);

    if (fileData == "UNKNOWN_ERROR" || fileName == "UNKNOWN_ERROR") {
      routing.replace("/");
      return;
    }

    this._fileName = fileName;
    this._editor = new Editor();

    if (fileData != "") {
      deserializeLayerGroup(fileData, this._editor);
    }

    this._questionnaireState = new QuestionnaireChatState(this._editor);

    if (this._editor.startLayer == null) {
      this._status = "NO_START_NODE";
      return;
    }

    this._status = "GET_NAME";

    if (this._status == "GET_NAME" && !this._editor.startLayer.enabledName) {
      this._name = "Anonymous Guest";
      this._qAnswer = "";
      this._status = "NORMAL";
    }
  }

  async componentDidUpdate(
    prevProps: Readonly<EditorPageProps>,
    prevState: Readonly<{ progress: number; responseLength: number }>
  ): Promise<any> {
    let scrollDiv = document.getElementById("scrollDiv");
    scrollDiv.scrollTop = scrollDiv.scrollHeight;
    const { questionType } =
      this._questionnaireState?.currentQuestionNode ?? {};
    if (this._status == "COMPLETE") {
      setInterval(() => {
        if (this.state.progress < 100) {
          this.setState({
            progress: this.state.progress + 10,
          });
        }
      }, 800);
      // setTimeout(() => {
      //   this.redirect()
      // }, 5000);
    } else if (this._status == "NORMAL" || this._status == "UPLOAD_LOADING") {
      if (prevState.responseLength !== this.messageArray.length) {
        this.setState({
          ...this.state,
          responseLength: this.messageArray.length,
        });
        await this.updateNarrativeData();
        this._status == "UPLOAD_LOADING" ? (this._status = "NORMAL") : null;
      }

      // new logic for question type === Without Response

      if (questionType && questionType === "Without Response") {
        // update current question
        this._qAnswer = "";
        // move to the next question
        this._questionnaireState?.currentQuestionNode && this.handleNext();
      }
    }
  }

  renderNavBar() {
    let { account } = this.props;
    return (
      <AppBar position="static">
        <Toolbar variant="dense">
          {account.user != null && <SaveButton kind="checked-back" />}
          <Typography variant="h6" style={{ flexGrow: 1, color: "inherit" }}>
            {this._status == "LOADING" ? "Loading..." : this._fileName}
          </Typography>

          <IconButton color="inherit" onClick={this.reload}>
            <ReloadIcon />
          </IconButton>
          {getPlatformType() == "BROWSER" && account.user != null && (
            <LogoutButton />
          )}
        </Toolbar>
      </AppBar>
    );
  }

  renderBotMessageComponent(message: string, replyOptions?) {
    return (
      <>
        <Box
          width="100%"
          padding="10px 20px"
          display="flex"
          flexDirection="row"
          alignItems="center"
        >
          {this._userData.displayPic ? (
            <Avatar alt="A" src={this._userData.displayPic}></Avatar>
          ) : (
            <Avatar style={{ backgroundColor: "#1A1A1A" }}>
              <Icon icon={robotIcon} />
            </Avatar>
          )}
          <Box
            bgcolor="#E9EEF3"
            borderRadius="40px"
            color="#5B5D65"
            textAlign="left"
            alignContent="center"
            padding="16px"
            margin="0px 10px"
            maxWidth="80%"
            className="containerWithSimpleLink"
          >
            <CustomLinkify str={message} />
          </Box>
        </Box>
      </>
    );
  }

  linearProgressWithLabel(progress: number) {
    return (
      <Box display="flex" alignItems="center">
        <Box width="100%" mr={1}>
          <LinearProgress variant="determinate" value={progress} />
        </Box>
        <Box minWidth={35}>
          <Typography variant="body2" color="textSecondary">{`${Math.round(
            progress
          )}%`}</Typography>
        </Box>
      </Box>
    );
  }

  renderInputMessageComponent(
    message: string,
    filesLinks: any = [],
    idx?: number,
    replyOptions?
  ) {
    const formatedMessage = `${message ? `${message}\n` : ""}${filesLinks.join(
      "\n"
    )}`;

    return (
      <>
        {(message || filesLinks.length > 0) && (
          <>
            <Box
              width="100%"
              padding="10px 20px"
              display="flex"
              flexDirection="row"
              alignItems="center"
              justifyContent="flex-end"
            >
              <Box
                className="containerWithLink"
                borderRadius="40px"
                color="#F9FCFF"
                textAlign="left"
                alignContent="center"
                padding="16px"
                margin="0px 10px"
                style={{
                  backgroundColor: "#1C8EFF",
                  opacity: `${
                    this._status === "UPLOAD_LOADING" &&
                    filesLinks.length > 0 &&
                    idx === this.messageArray.length - 1
                      ? "0.6"
                      : "1"
                  }`,
                }}
              >
                <CustomLinkify str={formatedMessage} />
              </Box>
              <Avatar style={{ backgroundColor: "#1A1A1A" }}>
                <PermIdentityIcon />
              </Avatar>
            </Box>
            {this._status === "UPLOAD_LOADING" &&
              filesLinks.length > 0 &&
              idx === this.messageArray.length - 1 && (
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="flex-end"
                  columnGap="6px"
                  paddingRight="20px"
                >
                  <CircularProgress style={{ width: "16px", height: "16px" }} />
                  <Typography
                    fontStyle="italic"
                    fontSize="14px"
                    color="#3f50b5"
                  >
                    Uploading ...
                  </Typography>
                </Box>
              )}
          </>
        )}
      </>
    );
  }

  renderQuestionnaireComponent(
    question: string,
    answer: string,
    filesLinks: any,
    i: number
  ) {
    return (
      <div key={i}>
        {question ? (
          <>
            {this.renderBotMessageComponent(question)}
            {this.renderInputMessageComponent(answer, filesLinks, i)}
            <div style={{ marginBottom: "12px" }}></div>
          </>
        ) : null}
      </div>
    );
  }

  renderLoading() {
    return (
      <LoaderContainer>
        <CircularProgress />
      </LoaderContainer>
    );
  }

  renderQuestionnaire() {
    return (
      <React.Fragment>
        <QuestionnaireChatRenderer
          editor={this._editor}
          profilePic={this._userData.displayPic}
          state={this._questionnaireState}
          optioOnClick={this.handleNext}
        />
        {((this._editor.startLayer.enabledName &&
          this.messageArray.length >= 0) ||
          (!this._editor.startLayer.enabledName &&
            this.messageArray.length >= 1)) && (
          <Button
            variant="contained"
            color="inherit"
            onClick={this.handlePrevious}
            style={{ marginLeft: "20px" }}
          >
            <ArrowBackIcon style={{ marginRight: "5px" }} /> GO BACK TO PREVIOUS
          </Button>
        )}
      </React.Fragment>
    );
  }

  renderGetName() {
    return this.renderBotMessageComponent("Hi! Please tell me your name.");
  }

  renderButtonPanel() {
    return (
      <ButtonRow>
        <NextButton onClick={this.handleNext}> NEXT </NextButton>
      </ButtonRow>
    );
  }

  renderNoStartNode() {
    return (
      <div
        style={{
          marginLeft: "12px",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        <QuestionText>
          You must first go to https://flowcharts.ai to create your flow chart,
          workflow, dynamic questionnaire or survey. Once you do that, you can
          then use this mobile companion to administer it from this app in order
          to collect results from your respondents
        </QuestionText>

        {getPlatformType() == "BROWSER" ? (
          <ButtonRow>
            <NextButton onClick={this.handleEditFlow}> EDIT FLOW </NextButton>
          </ButtonRow>
        ) : null}
      </div>
    );
  }

  render() {
    return (
      <Root style={{ position: "relative" }}>
        <div
          style={{
            width: "100%",
            height: "env(safe-area-inset-top)",
            backgroundColor: "#3f51b5",
          }}
          ref={(it) => (this._insetRef = it)}
        />
        {this.renderNavBar()}
        <div
          id="scrollDiv"
          style={{
            position: "relative",
            width: "100%",
            overflowY: "scroll",
            scrollbarWidth: "none",
            paddingTop: "24px",
            msOverflowStyle: "none",
          }}
        >
          <ChatBody>
            {this._status == "NO_START_NODE" ? this.renderNoStartNode() : null}
            {this._status == "LOADING" ? this.renderLoading() : null}
            {this._status !== "NO_START_NODE" &&
            this._status !== "LOADING" &&
            this._editor.startLayer.enabledName
              ? this.renderGetName()
              : null}
            {this._name !== "Anonymous Guest" || "" || null
              ? this.renderInputMessageComponent(this._name, [])
              : null}
            {this.messageArray.map((message, i) =>
              this.renderQuestionnaireComponent(
                message.question,
                message.answer,
                message.filesLinks,
                i
              )
            )}
            {this._status == "NORMAL" &&
            this._questionnaireState?.currentQuestionNode
              ? this.renderQuestionnaire()
              : null}
            {this._status === "GET_NAME" && (
              <ChatFoot style={{ margin: "0 1rem" }}>
                <input
                  id="scrollDiv"
                  value={this._qAnswer}
                  onChange={(ev) => (this._qAnswer = ev.target.value)}
                  onKeyDown={this.handleKeyPress}
                  // disableUnderline={true}
                  placeholder="Type your message here"
                  style={{
                    color: "#5B5D65",
                    backgroundColor: "transparent",
                    display: "flex",
                    flexGrow: 1,
                    minHeight: "60px",
                    margin: "4px 0",
                    border: "none",
                    outline: "none",
                    padding: "6px 20px",
                  }}
                />
                <Button
                  onClick={this.handleNext}
                  sx={{ borderRadius: "0 20px 20px 0" }}
                >
                  <SendIcon style={{ color: "#5B5D65" }}></SendIcon>
                </Button>
              </ChatFoot>
            )}
            {this._status === "NORMAL" &&
            this._questionnaireState.currentQuestionNode?.questionType ===
              questionTypes.text ? (
              <Box padding="1rem">
                {this.selectedUploadFile.length > 0 && (
                  <Box
                    display="flex"
                    bgcolor="#d3e2f0"
                    borderRadius="12px"
                    marginBottom="1rem"
                    paddingTop="1rem"
                    width="100%"
                    sx={{ overflowX: "auto" }}
                  >
                    {this.selectedUploadFile.map((file: any, idx: number) => (
                      <Box
                        key={idx}
                        minWidth="160px"
                        maxWidth="180px"
                        height="100px"
                        padding="8px 1rem"
                        position="relative"
                      >
                        <Cancel
                          style={{
                            position: "absolute",
                            top: "-6px",
                            right: "0",
                            color: "gray",
                            cursor: "pointer",
                          }}
                          onClick={() =>
                            (this.selectedUploadFile =
                              this.selectedUploadFile.filter(
                                (_: any, i: number) => i !== idx
                              ))
                          }
                        />
                        {file.type == "application/pdf" ? (
                          <img
                            src={docsImg}
                            width="100%"
                            height="100%"
                            style={{
                              objectFit: "contain",
                              border: "1px solid #ccc",
                              borderRadius: "6px",
                              backgroundColor: "#fff",
                            }}
                          />
                        ) : (
                          <img
                            src={URL.createObjectURL(file)}
                            width="100%"
                            height="100%"
                            style={{
                              objectFit: "contain",
                              border: "1px solid #ccc",
                              borderRadius: "6px",
                              backgroundColor: "#fff",
                            }}
                          />
                        )}
                      </Box>
                    ))}
                  </Box>
                )}

                <DragDropContainer
                  handleChange={this.getUploadedFile}
                  customClassName="custom-questionnaire-drop"
                >
                  <Box className="upload-field">
                    <textarea
                      id="scrollDiv"
                      value={this._qAnswer}
                      onChange={(ev) => (this._qAnswer = ev.target.value)}
                      placeholder="Type your message or drop some files here"
                      style={{
                        color: "#5B5D65",
                        backgroundColor: "transparent",
                        display: "flex",
                        flexGrow: 1,
                        minHeight: "80px",
                        margin: "10px 0",
                        border: "none",
                        outline: "none",
                        padding: "6px 20px",
                      }}
                      rows={3}
                    />
                    <input
                      type="file"
                      id="uploadFile"
                      name="uploadFile"
                      hidden
                      multiple
                      accept="image/*,.pdf,.doc,.docx,.xml,application/msword"
                      onChange={(ev) => this.getUploadedFile(ev.target.files)}
                    />
                    <label
                      htmlFor="uploadFile"
                      style={{
                        margin: "auto 0",
                        cursor: "pointer",
                      }}
                    >
                      <AttachFileRounded style={{ color: "#5B5D65" }} />
                    </label>
                    <Button
                      onClick={this.handleNext}
                      sx={{ borderRadius: "0 20px 20px 0" }}
                    >
                      <SendIcon style={{ color: "#5B5D65" }}></SendIcon>
                    </Button>
                  </Box>
                </DragDropContainer>
              </Box>
            ) : null}
          </ChatBody>
        </div>
        {this._status == "COMPLETE" ? (
          <>
            {this._editor.startLayer.referralUrlStatus &&
              !this._editor.startLayer.finalQuestionStatus && (
                <Dialog
                  open={this.endDialogOpen}
                  onClose={() => (this.endDialogOpen = false)}
                >
                  <>
                    <DialogTitle style={{ textAlign: "center" }}>
                      {this._editor && this._editor.startLayer.finalMessage
                        ? `${this._editor.startLayer.finalMessage}`
                        : "Thank you for your responses. Your responses are submitted."}
                    </DialogTitle>
                    {this.state.progress === 100 && this.redirect()}
                    <div
                      style={{
                        width: "400px",
                        margin: "0 auto",
                        padding: "0 20px",
                        marginBottom: "20px",
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "center",
                      }}
                    >
                      <h3
                        style={{
                          width: "fit-content",
                          margin: "0 auto",
                          marginBottom: "20px",
                        }}
                      >
                        Loading....
                      </h3>
                      {this.linearProgressWithLabel(this.state.progress)}
                    </div>
                  </>
                </Dialog>
              )}
            {!this._editor.startLayer.referralUrlStatus &&
              this._editor.startLayer.finalQuestionStatus && (
                <Box
                  width="100%"
                  display="flex"
                  justifyContent="center"
                  position="absolute"
                  bottom="0"
                  p="1.8rem"
                  fontSize="2rem"
                >
                  <Button
                    color="primary"
                    sx={{
                      width: "200px",
                      borderRadius: "30px",
                      padding: "1rem 6px",
                    }}
                    variant="contained"
                    onClick={() => {
                      window.close();
                      window.parent.postMessage({ surveyClosed: true }, "*");
                      // setTimeout(() => {
                      //   this.redirect();
                      // }, 1000);
                    }}
                  >
                    <Close sx={{ marginRight: "0.3rem" }} />
                    close
                  </Button>
                </Box>
              )}
            {!this._editor.startLayer.referralUrlStatus &&
              !this._editor.startLayer.finalQuestionStatus && (
                <Dialog
                  open={this.endDialogOpen}
                  onClose={() => (this.endDialogOpen = false)}
                >
                  <>
                    <DialogTitle style={{ textAlign: "center" }}>
                      {this._editor && this._editor.startLayer.finalMessage
                        ? `${this._editor.startLayer.finalMessage}`
                        : "Thank you for your responses. Your responses are submitted."}
                    </DialogTitle>
                  </>
                </Dialog>
              )}
          </>
        ) : null}
      </Root>
    );
  }
}
